/* eslint-disable react/prop-types */
import { FormikProps } from 'formik';
import { INewPerson } from '../../models/interfaces/INewPerson';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChoiceGroup, IChoiceGroupOption, TextField } from '@fluentui/react';
import styles from './SigningContractForm.module.scss';
import { IChoiceGroupOptionWithDescription } from './IChoiceGroupOptionWithDescription';
import { SigningContractType } from '../../constants/constants';

interface ISigningContractFormProps {
    formik: FormikProps<INewPerson>;
    title?: string;
    getErrorFieldProps: (formik, field) => void | { errorMessage: string };
    getFieldProps: (formik, field) => void | { errorMessage: string };
    handleGroupOptionOnChangeValue: (formik, fieldName) => (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IChoiceGroupOption) => void;
    parentObjectName: string;
}

const SigningContractForm = ({ formik, getFieldProps, parentObjectName, title, handleGroupOptionOnChangeValue }: ISigningContractFormProps): JSX.Element => {
    const { t } = useTranslation();

    const renderChoiceOption = (props, render): JSX.Element => {
        return (
            <div className={styles.radioButton}>
                {render(props)}
                <div className={styles.description} dangerouslySetInnerHTML={{ __html: props?.description }}></div>
            </div>
        );
    };

    const options: IChoiceGroupOptionWithDescription[] = [
        {
            key: SigningContractType.OwnQualifiedSignature,
            text: t('SigningContractType.OwnQualifiedSignatureLabel'),
            description: t('SigningContractType.OwnQualifiedSignatureDescription'),
            onRenderField: renderChoiceOption
        },
        {
            key: SigningContractType.SigniusQualifiedSignature,
            text: t('SigningContractType.SigniusQualifiedSignatureLabel'),
            description: t('SigningContractType.SigniusQualifiedSignatureDescription'),
            onRenderField: renderChoiceOption
        },
        {
            key: SigningContractType.InPersonAtTheOffice,
            text: t('SigningContractType.InPersonAtTheOfficeLabel'),
            description: t('SigningContractType.InPersonAtTheOfficeDescription'),
            onRenderField: renderChoiceOption
        }
    ];

    const shouldShowAdditionalFields =
        formik.values[parentObjectName] === SigningContractType.SigniusQualifiedSignature;

    return (
        <>
            <div className={styles.form}>
                <h2>{title}</h2>
                <div>
                    <ChoiceGroup
                        {...getFieldProps(formik, parentObjectName)}
                        className={styles.radioButtonGroup}
                        label={t('SigningContractType.Description')}
                        options={options}
                        required
                        onChange={handleGroupOptionOnChangeValue(formik, parentObjectName)}
                        selectedKey={formik.values[parentObjectName]}
                    />
                    {getFieldProps(formik, parentObjectName) && <div className={styles.errorMessage}>
                        {getFieldProps(formik, parentObjectName)?.errorMessage}</div>}
                </div>  
            </div>
            {shouldShowAdditionalFields && (
                <div className={styles.additionalFields}>
                    <TextField
                        label={t('SigningContractType.SigningEmailLabel')}
                        required
                        {...getFieldProps(formik, 'signingEmail')}
                        value={formik.values.signingEmail}
                        onChange={(_, newValue) => formik.setFieldValue('signingEmail', newValue)}
                        onBlur={() => formik.setFieldTouched('signingEmail', true)}
                    />
                    <TextField
                        label={t('SigningContractType.SigningPhoneNumberLabel')}
                        required
                        {...getFieldProps(formik, 'signingPhoneNumber')}
                        value={formik.values.signingPhoneNumber}
                        onChange={(_, newValue) => formik.setFieldValue('signingPhoneNumber', newValue)}
                        onBlur={() => formik.setFieldTouched('signingPhoneNumber', true)}
                    />
                </div>
            )}
        </>
    );
};

export default SigningContractForm;
